import * as vp from 'vopay';
import moment from 'moment';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useLayout } from '../../../../providers/Layout.provider';
import { useToken } from '../../../../providers/Token.provider';
import { usePaylink } from '../../../../providers/Paylink.provider';
import styles from './InstallmentPlan.module.scss';
import PaymentMethods from '../PaymentMethods/PaymentMethods';
import { useFlexiblePayment } from '../../../../providers/FlexiblePayment.provider';

const { Box, Title, Tooltip, Accordion } = vp;

type Frequency = 'biweekly' | 'monthly' | 'weekly';

type FC = FunctionComponent;

const InstallmentPlan: FC = () => {
  const { setLayoutInfo } = useLayout();
  const { token, isSetByBrowser } = useToken();
  const { paylink } = usePaylink();
  const { updateFlexiblePayment, updateInstallmentDetails } = useFlexiblePayment();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  // const [connectionMethod, setConnectionMethod] = useState<vp.ConnectionTypes | null>(null);

  const { t, i18n } = useTranslation('translations', { keyPrefix: 'InstallmentPlan' });
  useEffect(() => {
    setLayoutInfo(
      vp.chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
      'curve',
      '',
      70,
      32,
      0,
      !isSetByBrowser,
      true,
    );
  }, []);

  const [initialPayment, setInitialPayment] = useState('');
  const [scheduleAmount, setScheduleAmount] = useState('');
  const [scheduleStartDate, setScheduleStartDate] = useState('');
  const now = moment().format('MMM DD, YYYY');

  const frequencyMap: Record<Frequency, string> = {
    biweekly: t('biweekly', { defaultValue: 'Bi-Weekly' }),
    monthly: t('monthly', { defaultValue: 'Monthly' }),
    weekly: t('weekly', { defaultValue: 'Weekly' }),
  };

  const frequencyName = paylink.NameOfFrequency as Frequency;

  useEffect(() => {
    const calculateScheduleStartDate = () => {
      let newStartDate;

      switch (paylink.NameOfFrequency) {
        case 'weekly':
          newStartDate = moment(now).add(1, 'week').format('YYYY-MM-DD');
          break;
        case 'biweekly':
          newStartDate = moment(now).add(2, 'weeks').format('YYYY-MM-DD');
          break;
        case 'monthly':
          newStartDate = moment(now).add(1, 'month').format('YYYY-MM-DD');
          break;
        default:
          newStartDate = now;
      }
      return newStartDate;
    };
    setScheduleStartDate(calculateScheduleStartDate());
  }, [paylink]);

  useEffect(() => {
    if (paylink.Amount && paylink.EndingAfterPayments) {
      const totalAmount = Number(paylink.Amount);
      const endingAfterPayments = Number(paylink.EndingAfterPayments);

      const installmentAmount = totalAmount / (endingAfterPayments + 1);
      const formattedInstallmentAmount = installmentAmount.toFixed(3).slice(0, -1);

      const remainingAmount = totalAmount - Number(formattedInstallmentAmount) * (endingAfterPayments + 1);
      const initialAmount = (remainingAmount + Number(formattedInstallmentAmount)).toFixed(3).slice(0, -1);

      setScheduleAmount(formattedInstallmentAmount);
      setInitialPayment(String(initialAmount));
    }
  }, [paylink]);

  useEffect(() => {
    if (initialPayment && scheduleAmount && scheduleStartDate) {
      updateInstallmentDetails({
        initialAmount: initialPayment,
        scheduleAmount,
        scheduleStartDate,
      });
    }
  }, [initialPayment, scheduleAmount, scheduleStartDate]);

  const getDates = (frequency: string, endingAfterPayments: string) => {
    const dates = [];
    const currentDate = moment();

    for (let i = 1; i <= Number(endingAfterPayments); i += 1) {
      if (frequency === 'weekly') {
        currentDate.add(1, 'week');
      } else if (frequency === 'monthly') {
        currentDate.add(1, 'month');
      } else if (frequency === 'biweekly') {
        currentDate.add(2, 'weeks');
      }
      dates.push(currentDate.format('MMM DD, YYYY'));
    }
    return dates;
  };

  const scheduleDates = getDates(paylink.NameOfFrequency, paylink.EndingAfterPayments);

  return (
    <Box jc="center">
      <Box flx="col" jc="center" ai="center" base="8" gap="md" maxwidth="545px">
        <Box pt="md" base="8" gap="sm" ai="center" height="auto">
          <Title title={`${t('title')}`} size="lg" bold />
          <Tooltip
            information={`${t('installmentPlanDescription')}`}
            icon="question"
            position="left"
            variant="outlined"
            width="235px"
            fontSize="16px"
            positionX="-100%"
            positionY="-46%"
            textAlign="left"
          />
        </Box>
        <Box base="8" gap="common" flx="col" height="auto">
          <Box jc="center">
            <Box gapSize="10px" base="8" flx="col" boundary="shadow" p="md" r="md" maxwidth="358px" width="300px">
              <Box gapSize="4px" flx="col">
                <div className={styles.totalTitle}>{t('total')}</div>
                <div className={styles.totalAmount}>
                  {paylink.Currency} {vp.formattedCurrency(paylink.Amount)}
                </div>
              </Box>
              <Box flx="col" gapSize="8px" width="100%">
                <div className={styles.planInformation}>
                  <Box jc="sb">
                    <div className={styles.frequency}>{t('Frequency')}</div>
                    <div className={styles.nameOfFrequency}>{frequencyMap[frequencyName]}</div>
                  </Box>
                </div>
                <div className={[styles.planInformation, styles.Accordion].join(' ')}>
                  <Accordion
                    title={
                      <div className={styles.timelineTitle}>
                        {Number(paylink.EndingAfterPayments) + 1} {t('payments')}
                      </div>
                    }
                    angleText={
                      isOpen ? (
                        <div id="less-info" className={styles.moreInfo}>
                          {t('lessInfo')}
                        </div>
                      ) : (
                        <div id="more-info" className={styles.moreInfo}>
                          {t('moreInfo')}
                        </div>
                      )
                    }
                    angleColor="blue"
                    angleSize="9px"
                    open={isOpen}
                    onClick={() => {
                      setIsOpen(!isOpen);
                    }}
                    arrowPosition={isOpen ? 'top' : 'bottom'}
                    body={
                      <div className={styles.timelines}>
                        <div className={styles.timeline}>
                          <Box gapSize="8px" ai="center">
                            <span className={[styles.dot, styles.active].join(' ')} />
                            <span>{t('dueToday')}</span>
                          </Box>
                          <span>
                            {paylink.Currency} {vp.formattedCurrency(initialPayment || '')}
                          </span>
                        </div>
                        {scheduleDates.map((date) => (
                          <div className={styles.timeline} key={date}>
                            <Box gapSize="8px" ai="center">
                              <span className={styles.dot} />
                              <span>{date}</span>
                            </Box>
                            <span>
                              {paylink.Currency} {vp.formattedCurrency(scheduleAmount || '')}
                            </span>
                          </div>
                        ))}
                      </div>
                    }
                  />
                </div>
              </Box>
              <p className={styles.description}>
                {i18n.language === 'en' ? (
                  <>
                    You will be charged <b>{paylink.Currency}</b> <b>{vp.formattedCurrency(scheduleAmount)}</b>{' '}
                    {paylink.NameOfFrequency} from <b>{moment(scheduleStartDate).format('MMM DD, YYYY')}</b> until your
                    remaining balance is cleared.
                  </>
                ) : (
                  <>
                    Vous serez facturé(e) <b>{vp.formattedCurrency(scheduleAmount)}</b> <b>{paylink.Currency}</b>{' '}
                    {paylink.NameOfFrequency === 'weekly'
                      ? 'hebdomadairement'
                      : paylink.NameOfFrequency === 'biweekly'
                        ? 'bimensuellement'
                        : paylink.NameOfFrequency === 'monthly'
                          ? 'mensuellement'
                          : ''}{' '}
                    à partir du <b>{moment(scheduleStartDate).format('MMM DD, YYYY')}</b> jusqu&apos;à ce que votre
                    solde restant soit réglé.
                  </>
                )}
              </p>
            </Box>
          </Box>

          <div className={styles.selectionDesc}>{t('selection')}</div>

          <PaymentMethods
            flexiblePaymentMethod={vp.FlexiblePaymentMethods.PartialPayment}
            onClick={(connectionType: vp.ConnectionTypes) => {
              switch (connectionType) {
                case vp.ConnectionTypes.Bank:
                  history.push('/connect/processor/bank');
                  break;
                case vp.ConnectionTypes.Credit:
                  history.push({
                    pathname: '/connect/processor/credit',
                    state: { origin: 'token' },
                  });
                  break;
                case vp.ConnectionTypes.DebitCard:
                  history.push('/connect/processor/debitcard');
                  break;
                case vp.ConnectionTypes.Email:
                  history.push({
                    pathname: '/connect/processor/email',
                    state: { origin: 'token' },
                  });
                  break;
                default:
                  break;
              }
              updateFlexiblePayment(true, vp.FlexiblePaymentMethods.InstallmentPlan);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default InstallmentPlan;
